import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Cpu, ExternalLink } from "lucide-react";

export const devices = [
  {
    slug: "heltec-v3-v4",
    name: "Heltec WiFi LoRa 32 V3/V4",
    chipset: "ESP32-S3 + SX1262",
    price: "$22–28",
    bestFor: "Companions · Repeaters · For Beginners",
    notes: "OLED screen built-in. Plenty of GPIO. Most popular starter board on the network.",
    image: "https://images.unsplash.com/photo-1620283085439-39620a1e21c4?w=800&q=80",
  },
  {
    slug: "rak4631",
    name: "RAK4631 / WisBlock",
    chipset: "nRF52840 + SX1262",
    price: "$35–50",
    bestFor: "Solar Repeaters · Companions · Low Battery use",
    notes: "Industry-leading sleep current. Ideal for portable companion nodes that run for weeks on one charge.",
    image: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&q=80",
  },
  {
    slug: "t-beam",
    name: "LilyGo T-Beam (v1.1+)",
    chipset: "ESP32 + SX1276/SX1262",
    price: "$40–55",
    bestFor: "Portable · GPS-tagged",
    notes: "Onboard GPS and 18650 battery holder. Great for field-testing repeater locations.",
    image: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&q=80",
  },
  {
    slug: "xiao-s3-wio",
    name: "Xiao ESP32S3 + WIO LoRa",
    chipset: "ESP32-S3 + SX1262",
    price: "$18–25",
    bestFor: "Tiny builds · Hidden nodes",
    notes: "Thumb-sized. Perfect for stealth installs in birdhouses, weatherproof enclosures, or vehicle mounts.",
    image: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&q=80",
  },
];

export default function Devices() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24" data-testid="devices-page">
      <div className="overline mb-3">/devices — flashing & configuration</div>
      <h1 className="font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl tracking-tighter mb-6">
        Supported devices
      </h1>
      <p className="text-lg text-slate-300 max-w-3xl leading-relaxed mb-10">
        Below are the boards we recommend and actively test on the East Coast network. Pick one, follow the device
        guide, then apply the regional preset on the <Link to="/settings" className="text-[#FF5500] hover:text-[#FF7326] underline underline-offset-4">Settings page</Link>.
      </p>

      <div className="bg-[#13161C] border border-[#272E3A] p-6 lg:p-8 mb-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
        <div>
          <div className="overline mb-2">Quick start</div>
          <div className="text-white font-display font-bold text-xl">Use the web flasher first</div>
          <p className="text-slate-400 mt-2 max-w-xl">
            The official browser-based MeshCore flasher handles 90% of devices with one click. Plug your board in via
            USB, open Chrome, pick your board, and click Flash.
          </p>
        </div>
        <a
          href="http://flasher.meshcore.io/"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="web-flasher-btn"
          className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF5500] hover:bg-[#FF7326] text-white font-semibold transition-colors whitespace-nowrap"
        >
          Open Flasher <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 lg:gap-6">
        {devices.map((d) => (
          <Link
            key={d.slug}
            to={`/devices/${d.slug}`}
            data-testid={`device-card-${d.slug}`}
            className="group bg-[#13161C] border border-[#272E3A] hover:border-[#FF5500] transition-colors p-6"
          >
            <div className="flex items-start justify-between mb-4">
              <Cpu className="w-6 h-6 text-[#FF5500]" strokeWidth={1.5} />
              <span className="font-mono text-xs text-[#94A3B8]">{d.price}</span>
            </div>
            <h3 className="font-display font-bold text-white text-xl mb-1">{d.name}</h3>
            <div className="font-mono text-xs text-[#94A3B8] mb-4">{d.chipset}</div>
            <p className="text-slate-400 text-sm mb-4 leading-relaxed">{d.notes}</p>
            <div className="flex items-center justify-between border-t border-[#272E3A] pt-4">
              <span className="font-mono text-xs uppercase tracking-widest text-[#94A3B8]">{d.bestFor}</span>
              <ArrowRight className="w-4 h-4 text-[#FF5500] group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
